import React, { useState, useEffect, useCallback } from 'react';
import UserListFilters from './UserListFilters';
import { useNavigate } from 'react-router-dom';
import { adminUserPath } from '../../../constants/adminPaths';
import { adminListAppUsers } from '../../../api/appUsersApi';
import RoleTabs from './tabs/RoleTabs';
import './styles/UserList.css';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    role: 'all',
    dateRange: 'all',
    inactive: 'all'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedTab, setSelectedTab] = useState('owners');
  const navigate = useNavigate();

  useEffect(() => {
    // UX: when filters change, reset paging
    setPage(0);
  }, [searchTerm, filters, selectedTab]);

  const toMillis = (v) => {
    if (!v) return 0;
    if (typeof v?.toDate === 'function') return v.toDate().getTime();
    const ms = new Date(v).getTime();
    return Number.isFinite(ms) ? ms : 0;
  };

  const startOfLocalDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

  const getCreatedCutoff = (range) => {
    const now = new Date();
    if (range === 'today') return startOfLocalDay(now);
    if (range === 'week') return now.getTime() - 7 * 24 * 60 * 60 * 1000;
    if (range === 'month') return new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    if (range === 'year') return new Date(now.getFullYear(), 0, 1).getTime();
    return 0;
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { users: fetched } = await adminListAppUsers();
      const userData = fetched.filter((user) => {
        if (filters.status === 'all') return true;
        return (user.status || '').toLowerCase() === filters.status.toLowerCase();
      });

      // Apply search + inactive filters
      const baseFiltered = userData.filter(user => {
        const searchString = searchTerm.toLowerCase();
        const matchesSearch = (
          user.email?.toLowerCase().includes(searchString) ||
          user.firstName?.toLowerCase().includes(searchString) ||
          user.lastName?.toLowerCase().includes(searchString) ||
          user.companyName?.toLowerCase().includes(searchString)
        );
        if (!matchesSearch) return false;

        // Role filter (simple admin vs non-admin)
        if (filters.role && filters.role !== 'all') {
          const role = typeof user.role === 'string' ? user.role.toLowerCase() : '';
          if (filters.role === 'admin' && role !== 'admin') return false;
          if (filters.role === 'user' && role === 'admin') return false;
        }

        // Date range filter (createdAt)
        if (filters.dateRange && filters.dateRange !== 'all') {
          const cutoff = getCreatedCutoff(filters.dateRange);
          const createdMs = toMillis(user.createdAt);
          if (cutoff && (!createdMs || createdMs < cutoff)) return false;
        }

        if (filters.inactive === 'all') return true;

        const days = parseInt(filters.inactive, 10);
        if (!Number.isFinite(days)) return true;

        const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
        const lastActive = user.lastActiveAt?.toDate ? user.lastActiveAt.toDate().getTime() : (user.lastActiveAt ? new Date(user.lastActiveAt).getTime() : 0);
        // Treat users with no activity as oldest (include them when filtering for inactivity)
        const lastActiveOrZero = Number.isFinite(lastActive) ? lastActive : 0;
        return lastActiveOrZero <= cutoff;
      });

      // Classify users for tabs
      const toLower = (v) => (typeof v === 'string' ? v.toLowerCase() : v);
      const getCategory = (u) => {
        const role = toLower(u.role);
        const type = toLower(u.type);
        const accountType = toLower(u.accountType);
        const isOwner = role === 'owner';
        const isEmployee = type === 'employee' || role === 'employee' || (!!u.companyId && !isOwner);
        const isClient = accountType === 'client' || role === 'client' || u.isClientOnly === true;
        if (isOwner) return 'owners';
        if (isEmployee) return 'employees';
        if (isClient) return 'clients';
        return 'unknown';
      };

      // Tab counts based on current search/inactive filters
      const counts = baseFiltered.reduce((acc, u) => {
        const cat = getCategory(u);
        acc.all += 1;
        if (cat === 'owners') acc.owners += 1;
        if (cat === 'employees') acc.employees += 1;
        if (cat === 'clients') acc.clients += 1;
        return acc;
      }, { all: 0, owners: 0, employees: 0, clients: 0 });

      // Store counts on state via a symbol property (avoid extra state for simplicity)
      baseFiltered._counts = counts; // non-enumerated in React state usage not guaranteed; we will recompute on render if needed

      // Apply tab filter
      const tabFiltered = selectedTab === 'all' ? baseFiltered : baseFiltered.filter(u => getCategory(u) === selectedTab);

      setUsers(Object.assign([], tabFiltered, { _counts: counts }));
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, selectedTab]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (next) => {
    setRowsPerPage(parseInt(next, 10));
    setPage(0);
  };

  const handleViewProfile = (userId) => {
    navigate(adminUserPath(userId));
  };

  const total = users.length;
  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, total);
  const pageCount = Math.max(1, Math.ceil(total / rowsPerPage));
  const canPrev = page > 0;
  const canNext = endIndex < total;
  const pagedUsers = users.slice(startIndex, endIndex);

  const onPrev = () => setPage((p) => Math.max(0, p - 1));
  const onNext = () => setPage((p) => Math.min(pageCount - 1, p + 1));

  const fmtDate = (v) => {
    const ms = toMillis(v);
    if (!ms) return '—';
    return new Date(ms).toLocaleDateString();
  };

  const fmtDateTime = (v) => {
    const ms = toMillis(v);
    if (!ms) return '—';
    return new Date(ms).toLocaleString();
  };

  const getDerivedStatus = (user) => {
    const isBlocked = user.status === 'blocked';
    const hasLastActive = Boolean(user.lastActiveAt);
    return isBlocked ? 'blocked' : (!hasLastActive ? 'inactive' : (user.status || 'active'));
  };

  return (
    <div className="admin-page userListPage">
      <div className="admin-page__header">
        <div>
          <h1 className="admin-page__title">Users</h1>
          <p className="admin-page__subtitle">Browse account owners, employees, and client users in one place.</p>
        </div>
        <div className="userListHeaderRight">
          <span className="userListPill">Total: {total}</span>
        </div>
      </div>

      <div className="userListCard" aria-label="User list controls">
        <div className="userListControlsRow">
          <div className="userListSearch">
            <label htmlFor="userSearch">Search</label>
            <input
              id="userSearch"
              type="search"
              placeholder="Search by name, email, or company…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="userListBtnRow">
            <button
              type="button"
              className="userListBtn"
              onClick={() => setShowFilters((v) => !v)}
              aria-expanded={showFilters ? 'true' : 'false'}
            >
              {showFilters ? 'Hide filters' : 'Filters'}
            </button>
            <button type="button" className="userListBtn userListBtnPrimary" onClick={fetchUsers} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <RoleTabs
            value={selectedTab}
            onChange={setSelectedTab}
            counts={(users && users._counts) || { all: users.length, owners: 0, employees: 0, clients: 0 }}
          />
        </div>

        {showFilters ? (
          <UserListFilters
            filters={filters}
            setFilters={setFilters}
          />
        ) : null}
      </div>

      {loading ? (
        <div className="table-wrap">
          <table className="data-table userListTable" aria-label="Users table (loading)">
            <thead>
              <tr>
                <th>User</th>
                <th className="colEmail">Email</th>
                <th className="colCompany">Company</th>
                <th>Status</th>
                <th className="colLastActive">Last Active</th>
                <th className="colCreated">Created</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: Math.min(rowsPerPage, 10) }).map((_, idx) => (
                <tr key={idx}>
                  <td><div className="userSkeletonBar" style={{ width: 220 }} /></td>
                  <td className="colEmail"><div className="userSkeletonBar" style={{ width: 240 }} /></td>
                  <td className="colCompany"><div className="userSkeletonBar" style={{ width: 160 }} /></td>
                  <td><div className="userSkeletonBar" style={{ width: 110 }} /></td>
                  <td className="colLastActive"><div className="userSkeletonBar" style={{ width: 150 }} /></td>
                  <td className="colCreated"><div className="userSkeletonBar" style={{ width: 110 }} /></td>
                  <td style={{ textAlign: 'right' }}><div className="userSkeletonBar" style={{ width: 190, marginLeft: 'auto' }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data-table userListTable" aria-label="Users table">
            <thead>
              <tr>
                <th>User</th>
                <th className="colEmail">Email</th>
                <th className="colCompany">Company</th>
                <th>Status</th>
                <th className="colLastActive">Last Active</th>
                <th className="colCreated">Created</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedUsers.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="userEmpty">No users found.</div>
                  </td>
                </tr>
              ) : (
                pagedUsers.map((user) => {
                  const initials = (user.firstName?.[0] || user.email?.[0] || 'U').toUpperCase();
                  const derivedStatus = getDerivedStatus(user);
                  const label = derivedStatus.charAt(0).toUpperCase() + derivedStatus.slice(1);

                  return (
                    <tr key={user.id} onClick={() => handleViewProfile(user.id)}>
                      <td>
                        <div className="userCell">
                          <div className="userAvatar" aria-hidden="true">
                            {user.photoURL ? <img src={user.photoURL} alt="" /> : initials}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div className="userName" title={`${user.firstName || ''} ${user.lastName || ''}`.trim()}>
                              {(user.firstName || user.lastName) ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : (user.email || 'User')}
                            </div>
                            <div className="userSub">{user.companyId ? user.companyName : 'No Company'}</div>
                            <div className="userSubMobile">
                              <div className="userSub">{user.email || '—'}</div>
                              <div className="userSub">{fmtDateTime(user.lastActiveAt)}</div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="colEmail" title={user.email || ''}>{user.email || '—'}</td>
                      <td className="colCompany">{user.companyId ? user.companyName : 'No Company'}</td>
                      <td>
                        <span className={`statusPill statusPill--${derivedStatus}`}>{label}</span>
                      </td>
                      <td className="colLastActive">{fmtDateTime(user.lastActiveAt)}</td>
                      <td className="colCreated">{fmtDate(user.createdAt)}</td>
                      <td>
                        <div className="userActions" aria-label="User actions">
                          <button
                            type="button"
                            className="userActionBtn"
                            title="View Profile"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewProfile(user.id);
                            }}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            className="userActionBtn userActionBtnMuted"
                            title="Block user (coming soon)"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Block
                          </button>
                          <button
                            type="button"
                            className="userActionBtn userActionBtnDanger userActionBtnMuted"
                            title="Delete user (coming soon)"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          <div className="userListCard" style={{ borderRadius: 0, boxShadow: 'none', border: 'none', padding: 16 }}>
            <div className="userFooter">
              <div className="userListPill">
                Showing <strong>{total === 0 ? 0 : startIndex + 1}</strong>–<strong>{endIndex}</strong> of{' '}
                <strong>{total}</strong>
              </div>
              <div className="userPager" aria-label="Pagination">
                <span className="userListPill">
                  Rows:{' '}
                  <select
                    className="userListSelect"
                    value={rowsPerPage}
                    onChange={(e) => handleChangeRowsPerPage(e.target.value)}
                    aria-label="Rows per page"
                    style={{ width: 90, marginLeft: 8 }}
                  >
                    {[5, 10, 25].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </span>
                <button type="button" className="userPagerBtn" onClick={onPrev} disabled={!canPrev}>
                  Prev
                </button>
                <span className="userListPill" aria-label="Current page">
                  Page {Math.min(page + 1, pageCount)} / {pageCount}
                </span>
                <button type="button" className="userPagerBtn" onClick={onNext} disabled={!canNext}>
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList; 